// eslint-disable-next-line max-classes-per-file
import { AlertModal, Box, ErrorModal, Text, Toast, useOpenModal } from '@modusign/design-system';
import { createThirdPartyUser } from '@modusign/openapi-client-user';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useMount } from 'react-use';

import { useUrlQuery } from '@hooks';

import useThirdPartySignIn from '@app/features/authentication/hooks/useThirdPartySignIn';
import { disableKakaoAutoLogin } from '@app/hooks/useKakaoAutoLogin';
import useUserEmailConfirmation from '@app/hooks/useUserEmailConfirmation';
import { isKakaoInAppBrowser } from '@app/libs/browserUserAgent';
import closeKakaoInAppBrowser from '@app/libs/closeKakaoInAppBrowser';
import delay from '@app/libs/delay';
import { loginToWorkspace } from '@app/libs/login';
import {
  KakaoOAuthState,
  redirectToKakaoOAuthPage,
} from '@app/libs/oauth/redirectToKakaoOAuthPage';
import workspaceService from '@app/libs/workspaceService';
import { thirdPartySignInSuccess } from '@app/store/account/third-party/actions';
import { shareDocument } from '@app/store/transport-layer/document/documents/share';

class SignUpError extends Error {
  error: AxiosError<{ data?: { failureEmail?: string } }>;

  constructor(error: AxiosError<{ data?: { failureEmail?: string } }>) {
    super(error.message);
    this.error = error;
  }
}

class ShareDocumentError extends Error {
  error: AxiosError;

  constructor(error: AxiosError) {
    super(error.message);
    this.error = error;
  }
}

export default function KakaoCallback() {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useUrlQuery();

  const { setCount: setUserEmailConfirmationCountCount } = useUserEmailConfirmation();

  const openModal = useOpenModal();

  const thirdPartySignIn = useThirdPartySignIn();

  const { mutate: signUp } = useMutation<
    unknown,
    SignUpError | ShareDocumentError,
    {
      code: string;
      documentToShare?: { documentId: string; participantId: string; isLastOrder: boolean };
    }
  >(
    async ({ code, documentToShare }) => {
      // sign up
      const { result: user } = await createThirdPartyUser({
        code,
        agreement: { emailMarketing: false },
        provider: 'kakao',
      }).catch((e) => {
        throw new SignUpError(e);
      });

      // share document if exists
      let shareDocumentPromise: Promise<unknown>;
      if (documentToShare) {
        shareDocumentPromise = shareDocument({
          documentId: documentToShare.documentId,
          participantId: documentToShare.participantId,
          targetUserId: user.userId,
          workspaceId: workspaceService.getWorkspaceId(),
        })
          // 문서를 공유한 후 opensearch에 비동기적으로 반영되기 때문에 문서 목록에 안보일 수 있습니다.
          // 문서 목록을 불렀을 때 공유한 문서가 보이도록 의도적으로 딜레이를 줍니다.
          // 참고: https://app.shortcut.com/modusignform/story/21799/11
          // 참고: https://modusign.slack.com/archives/C024WC3TUSU/p1666592993377899
          .then(() => delay(400))
          .catch((e) => {
            throw new ShareDocumentError(e);
          });
      } else {
        shareDocumentPromise = Promise.resolve();
      }

      // log in
      await shareDocumentPromise.finally(() => {
        loginToWorkspace({
          type: 'token',
          tokens: { accessToken: user.accessToken, refreshToken: user.refreshToken },
        });
        dispatch(thirdPartySignInSuccess());
        setUserEmailConfirmationCountCount(3, user.userId);
      });
    },
    {
      onSettled: (_, error, { documentToShare: document }) => {
        if (error instanceof SignUpError) {
          switch (error.error.message) {
            // 이미 가입한 이메일일 때
            case 'User already exists': {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const email = error.error.response!.data.data!.failureEmail!;
              openModal(
                (props) => (
                  <AlertModalWithBox
                    {...props}
                    content="해당 이메일 주소로 가입한 계정이 있습니다. 이전에 가입했던 방식으로 로그인해 주세요."
                    email={email}
                    title="존재하는 이메일"
                  />
                ),
                { keepOnUnmount: true }
              );
              history.replace(`/authentication/signin?email=${email}`);
              return;
            }
            default:
              openModal(
                ({ isOpen, onClose }) => (
                  <ErrorModal
                    error={error.error}
                    isOpen={isOpen}
                    title="회원가입 실패"
                    onClose={onClose}
                  />
                ),
                { keepOnUnmount: true }
              );
              history.replace(`/authentication/signin`);
              return;
          }
        }

        if (error instanceof ShareDocumentError) {
          if (error.error.response?.status === 422) {
            Toast.warning({ description: '이미 문서함으로 옮긴 문서입니다.' });
          } else {
            Toast.error({
              title: '네트워크 문제 발생',
              description:
                '서명 완료된 문서를 내 모두싸인 문서함으로 옮기지 못했습니다. 다음에 다시 시도해 주세요.',
            });
          }
        } else if (document?.isLastOrder === false) {
          Toast.warning({
            title: '상대 서명 대기 중',
            description: '모든 서명자가 서명을 완료하면 내가 서명한 문서를 확인할 수 있습니다.',
          });
        }

        history.replace('/documents');
      },
    }
  );

  useMount(() => {
    const code = query.get('code');
    const state = JSON.parse(query.get('state') || '{}') as KakaoOAuthState;

    switch (state.mode) {
      case 'autoLogin':
        // 자동 로그인 한번 시도 후 재시도하지 않도록 합니다.
        disableKakaoAutoLogin();
        if (code) {
          thirdPartySignIn.mutate({ provider: 'kakao', code, redirectTo: state.redirectTo });
        } else {
          // 자동로그인 실패 시 유저가 카카오 회원이 아닌 경우이므로 바로 redirect 합니다.
          history.replace(state.redirectTo);
        }
        break;
      case 'autoLoginOrSignUp':
        // 자동 로그인 한번 시도 후 재시도하지 않도록 합니다.
        disableKakaoAutoLogin();
        if (code) {
          thirdPartySignIn.mutate({ provider: 'kakao', code, redirectTo: '/documents' });
        } else {
          // 카카오 회원이 아닌 경우 회원가입 시도
          redirectToKakaoOAuthPage({ mode: 'signUp' });
        }
        break;
      case 'signUp':
        if (code) {
          signUp({
            code,
          });
        } else {
          // 가입 취소했을 때
          // eslint-disable-next-line no-lonely-if
          if (isKakaoInAppBrowser()) {
            closeKakaoInAppBrowser();
          } else {
            history.replace('/documents');
          }
        }
        break;
      case 'postmessage': {
        // 유저가 로그인을 취소했을 때 access_denied 오류가 발생합니다.
        // 참고: https://developers.kakao.com/docs/latest/ko/kakaologin/rest-api#request-code-response
        // 참고: https://developers.kakao.com/docs/latest/ko/kakaologin/trouble-shooting
        const error = query.get('error') as 'access_denied';
        if (error === 'access_denied' || !code) {
          window.close();
        }

        // IE와 Edge에서 팝업이 다른 도메인으로 리디렉션 되면 opener를 보안적인 이유로 넘겨주지않는 이슈로
        // window.opener가 없으면, 쿼리를 통해 전달받은 windowName를 통해 부모 윈도우를 획득
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const opener = window.opener || window.open('', state.windowName);

        if (opener) {
          opener.postMessage({ code, state: JSON.stringify(state) });
          window.close();
        }
        break;
      }
      case 'signUpAndShareDocument':
        if (code) {
          signUp({
            code,
            documentToShare: {
              documentId: state.documentId,
              isLastOrder: state.isLastOrder,
              participantId: state.participantId,
            },
          });
        } else {
          // 가입 취소했을 때
          history.replace('/documents');
        }
        break;
      default:
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        throw new Error(`Kakao oauth redirect mode "${state.mode}" is not supported`);
    }
  });

  return null;
}

function AlertModalWithBox({
  title,
  content,
  isOpen,
  onClose,
  email,
}: {
  title: string;
  content: string;
  isOpen: boolean;
  onClose: () => void;
  email: string;
}) {
  return (
    <AlertModal isOpen={isOpen} maxWidth="xxs" title={title} variant="warning" onClose={onClose}>
      <Text textAlign="center">{content}</Text>
      <Box
        bg="boxBackgroundNeutralBase"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        mt={6}
        px={6}
        py={7}
      >
        {email}
      </Box>
    </AlertModal>
  );
}
